import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Alert, Card, Descriptions, List, Space, Spin, Tag, Typography } from 'antd';
import { TeamOutlined, QuestionCircleOutlined, GiftOutlined } from '@ant-design/icons';
import { PublicHomeLayout } from '../../components/layout/PublicHomeLayout';
import { SurveyLandingCTA } from '../../components/survey/SurveyLandingCTA';
import { RichTextDisplay } from '../../components/common/RichTextDisplay';
import { surveyLandingService } from '../../services/survey-landing.service';
import { engagementService } from '../../services/engagement.service';
import { LandingQuestionGroup, SurveyLandingData } from '../../types/survey-landing.types';
import { PublicEngagementCurrentCycle } from '../../types/engagement.types';
import { useOpenGraph } from '../../hooks/useOpenGraph';
import { useAuth } from '../../hooks/useAuth';
import { saveReferralCookie } from '../../utils/referral-cookie';

const { Title, Text } = Typography;

function formatGroupScope(scope: 'NATIONAL' | 'STATE', stateName: string | null): string {
  if (scope === 'NATIONAL') return 'Nacional';
  return stateName ? `Estadual — ${stateName}` : 'Estadual';
}

function renderQuestionPreview(
  groups: LandingQuestionGroup[],
  flatQuestions: { id: string; order: number; shortDescription: string }[]
) {
  const totalQuestions =
    groups.length > 0
      ? groups.reduce((acc, g) => acc + g.questions.length, 0)
      : flatQuestions.length;

  if (totalQuestions === 0) return null;

  return (
    <Card
      size="small"
      style={{ marginBottom: 24, background: '#fafafa' }}
      styles={{ body: { padding: '12px 16px' } }}
    >
      <Space style={{ marginBottom: 12 }}>
        <QuestionCircleOutlined style={{ color: '#1677ff' }} />
        <Text strong style={{ fontSize: 13 }}>
          O que será perguntado ({totalQuestions}{' '}
          {totalQuestions === 1 ? 'pergunta' : 'perguntas'})
        </Text>
      </Space>

      {groups.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {groups.map((group) => (
            <div key={group.id}>
              <Space size={6} style={{ marginBottom: 4 }}>
                {group.name && (
                  <Text strong style={{ fontSize: 12 }}>
                    {group.name}
                  </Text>
                )}
                <Tag
                  color={group.scope === 'NATIONAL' ? 'blue' : 'green'}
                  style={{ fontSize: 11, lineHeight: '18px', padding: '0 6px' }}
                >
                  {formatGroupScope(group.scope, group.stateName)}
                </Tag>
              </Space>
              <List
                size="small"
                dataSource={group.questions}
                split={false}
                renderItem={(q) => (
                  <List.Item style={{ padding: '3px 0' }}>
                    <Text style={{ fontSize: 13 }}>
                      <Text type="secondary" style={{ marginRight: 8, fontSize: 12 }}>
                        {q.order}.
                      </Text>
                      {q.shortDescription}
                    </Text>
                  </List.Item>
                )}
              />
            </div>
          ))}
        </div>
      ) : (
        <List
          size="small"
          dataSource={flatQuestions}
          split={false}
          renderItem={(q) => (
            <List.Item style={{ padding: '4px 0' }}>
              <Text style={{ fontSize: 13 }}>
                <Text type="secondary" style={{ marginRight: 8, fontSize: 12 }}>
                  {q.order}.
                </Text>
                {q.shortDescription}
              </Text>
            </List.Item>
          )}
        />
      )}
    </Card>
  );
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('pt-BR');
}

const SCOPE_LABELS: Record<string, string> = {
  NATIONAL: 'Nacional',
  STATE: 'Estadual',
  CITY: 'Municipal',
};

function formatScope(scope: SurveyLandingData['scope']): string {
  if (!scope) return 'Não informada';
  if (typeof scope === 'string') return SCOPE_LABELS[scope] ?? scope;
  const label = typeof scope.label === 'string' ? scope.label : undefined;
  if (label) return label;
  const type = typeof scope.type === 'string' ? scope.type : undefined;
  return type ? SCOPE_LABELS[type] ?? type : 'Não informada';
}

export function SurveyLandingPage() {
  const { surveyHash } = useParams<{ surveyHash: string }>();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [data, setData] = useState<SurveyLandingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [engagementCycle, setEngagementCycle] = useState<PublicEngagementCurrentCycle | null>(null);

  useOpenGraph(
    data
      ? {
          title: `${data.name} — PePoll`,
          description: data.description ?? 'Participe desta pesquisa de opinião na PePoll.',
          url: window.location.href,
        }
      : {}
  );

  useEffect(() => {
    if (!surveyHash) return;
    surveyLandingService
      .getLandingData(surveyHash)
      .then(setData)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [surveyHash]);

  useEffect(() => {
    if (!isAuthenticated) {
      const ref = searchParams.get('ref');
      if (ref) saveReferralCookie(ref);
    }
  }, [isAuthenticated, searchParams]);

  useEffect(() => {
    engagementService
      .getPublicCurrentCycle()
      .then(setEngagementCycle)
      .catch(() => setEngagementCycle(null));
  }, []);

  if (loading) {
    return (
      <PublicHomeLayout>
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <Spin size="large" />
        </div>
      </PublicHomeLayout>
    );
  }

  if (notFound || !data) {
    return (
      <PublicHomeLayout>
        <div style={{ maxWidth: 680, margin: '48px auto', padding: '0 16px' }}>
          <Alert
            type="error"
            message="Pesquisa não encontrada"
            description="O link que você acessou não corresponde a nenhuma pesquisa disponível."
            showIcon
          />
        </div>
      </PublicHomeLayout>
    );
  }

  return (
    <PublicHomeLayout>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 16px' }}>
        {data.status === 'COMPLETED' && (
          <Alert
            type="info"
            message="Esta pesquisa está encerrada"
            description="O período de participação foi finalizado. Você ainda pode consultar a transparência pública dos resultados."
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        <Card>
          <Space direction="vertical" size={4} style={{ marginBottom: 20 }}>
            <Title level={2} style={{ margin: 0 }}>
              {data.name}
            </Title>
            <Space>
              <TeamOutlined />
              <Text type="secondary">{data.responseCount} participações</Text>
            </Space>
          </Space>

          {data.description && (
            <div style={{ marginBottom: 24 }}>
              <RichTextDisplay html={data.description} />
            </div>
          )}

          {renderQuestionPreview(data.questionGroups, data.questions)}

          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Abrangência">
              {formatScope(data.scope)}
            </Descriptions.Item>
            <Descriptions.Item label="Período">
              {formatDate(data.startDate)}
              {data.endDate ? ` até ${formatDate(data.endDate)}` : ''}
            </Descriptions.Item>
            <Descriptions.Item label="Resultado">
              {data.visibility.disclosureRuleText}
            </Descriptions.Item>
          </Descriptions>

          <div
            style={{
              marginTop: 20,
              padding: '18px 20px',
              background: 'linear-gradient(135deg, #FFFBEA 0%, #FFF3C4 100%)',
              border: '1px solid #FFD666',
              borderRadius: 8,
            }}
          >
            <Space align="center" size={10} style={{ marginBottom: 10 }}>
              <GiftOutlined style={{ color: '#FFC200', fontSize: 22 }} />
              <Title level={5} style={{ margin: 0, color: '#2E2B81' }}>
                Programa de Recompensas PePoll
              </Title>
            </Space>

            <Text style={{ fontSize: 13.5, display: 'block', marginBottom: engagementCycle ? 14 : 0 }}>
              Ao responder pesquisas e indicar novos usuários você recebe{' '}
              <Text strong style={{ fontSize: 13.5 }}>
                bilhetes
              </Text>{' '}
              e concorre a{' '}
              <Text strong style={{ fontSize: 13.5 }}>
                prêmios em dinheiro
              </Text>{' '}
              no programa de engajamento da PePoll.
            </Text>

            {engagementCycle && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {engagementCycle.prizes.length > 0 && (
                  <div>
                    <Text strong style={{ fontSize: 12 }}>
                      Prêmios deste ciclo
                    </Text>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                      {engagementCycle.prizes.map((p) => (
                        <div
                          key={p.prizeRank}
                          style={{
                            background: '#ffffff',
                            border: '1px solid #FFE58F',
                            borderRadius: 6,
                            padding: '4px 10px',
                          }}
                        >
                          <Text type="secondary" style={{ fontSize: 11.5 }}>
                            {p.prizeRank}º
                          </Text>{' '}
                          <Text strong style={{ fontSize: 13.5, color: '#141414' }}>
                            {p.prizeValue}
                          </Text>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Space size={16} wrap>
                  <Text style={{ fontSize: 12 }}>
                    <Text strong style={{ fontSize: 12 }}>
                      Período do ciclo:{' '}
                    </Text>
                    {formatDate(engagementCycle.startsAt)} até {formatDate(engagementCycle.endsAt)}
                  </Text>

                  {engagementCycle.lotteryDrawDate && (
                    <Text style={{ fontSize: 12 }}>
                      <Text strong style={{ fontSize: 12 }}>
                        Data do sorteio:{' '}
                      </Text>
                      {formatDate(engagementCycle.lotteryDrawDate)}
                      {engagementCycle.lotteryDrawNumber
                        ? ` — Concurso nº ${engagementCycle.lotteryDrawNumber} (Loteria Federal)`
                        : ''}
                    </Text>
                  )}

                  {engagementCycle.spaRegistrationNumber && (
                    <Text style={{ fontSize: 12 }}>
                      <Text strong style={{ fontSize: 12 }}>
                        Registro SPA:{' '}
                      </Text>
                      {engagementCycle.spaRegistrationNumber}
                    </Text>
                  )}
                </Space>
              </div>
            )}
          </div>
        </Card>

        <SurveyLandingCTA
          surveyId={data.surveyId}
          surveyHash={data.surveyHash}
          hasUserParticipated={data.hasUserParticipated}
          status={data.status}
          allowGuestParticipation={data.allowGuestParticipation}
        />
      </div>
    </PublicHomeLayout>
  );
}
